import { Box, Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditLubricantGroupModal from '../../components/lubricants/EditLubricantGroupModal';
import PageHeader from '../../components/PageHeader';
import {
  useCreateLubricantGroupMutation,
  useDeleteLubricantGroupMutation,
  useGetLubricantGroupsListQuery,
  useUpdateLubricantGroupMutation,
} from '../../store/apiSlices/lubricants/lubricantGroups/lubricantGroupsApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TruncatedCellText } from '../../components/common/styled/TruncatedCellText';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface LubricantGroup {
  id: number;
  groupName: string;
  remarks: string;
}

interface ActionButton {
  label: string;
  variant: 'text' | 'blue' | 'outlinedBlue' | 'red' | 'gray' | undefined;
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
type GroupFormValues = {
  groupName: string;
  remarks: string;
};

const LubricantGroupsContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddGroupModal, setShowAddGroupModal] = useState<boolean>(false);
  const [editingGroup, setEditingGroup] = useState<LubricantGroup | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GroupFormValues>({
    defaultValues: {
      groupName: '',
      remarks: '',
    },
  });

  // API calls
  const {
    data: lubricantGroupsListResult,
    isLoading,
    refetch,
  } = useGetLubricantGroupsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [createLubricantGroup, { isLoading: isCreating }] = useCreateLubricantGroupMutation();
  const [updateLubricantGroup, { isLoading: isUpdating }] = useUpdateLubricantGroupMutation();
  const [deleteLubricantGroup, { isLoading: isDeleting }] = useDeleteLubricantGroupMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<LubricantGroup>({
      deleteMutation: deleteLubricantGroup,
      refetch,
      itemName: 'group',
      idField: 'id',
    });

  // Extract data from response
  const lubricantGroupsList = lubricantGroupsListResult?.data || [];

  // Ensure API refetches when needed
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('lubricants.addGroup'),
          variant: 'blue',
          onClick: () => {
            reset({
              groupName: '',
              remarks: '',
            });
            setShowAddGroupModal(true);
          },
        },
      ]
    : [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('lubricants.groups'), isActive: true, onClick: () => {} },
  ];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: LubricantGroup) => {
      setEditingGroup(rowData);
      reset({
        groupName: rowData.groupName,
        remarks: rowData.remarks,
      });
      setShowAddGroupModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        flex: 0,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'groupName',
        headerName: t('table.columns.group'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'remarks',
        headerName: t('table.columns.remarks'),
        width: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
        cellRenderer: (params: any) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <TruncatedCellText>{params?.value}</TruncatedCellText>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Form submission handler
  const onSubmit = useCallback(
    async (values: GroupFormValues) => {
      try {
        if (editingGroup) {
          const res: any = await updateLubricantGroup({
            id: editingGroup.id,
            name: values.groupName,
            remarks: values.remarks,
          }).unwrap();
          showSuccessToast(res?.message);
        } else {
          const res: any = await createLubricantGroup({
            name: values.groupName,
            remarks: values.remarks,
          }).unwrap();
          showSuccessToast(res?.message);
        }

        refetch();
        setShowAddGroupModal(false);
        setEditingGroup(null);
        reset({
          groupName: '',
          remarks: '',
        });
      } catch (error) {
        logger.error('Failed to submit group', error as Error);
      }
    },
    [createLubricantGroup, updateLubricantGroup, editingGroup, refetch, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddGroupModal(false);
    setEditingGroup(null);
    reset({
      groupName: '',
      remarks: '',
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={lubricantGroupsList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="lubricant-groups-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('lubricants.deleteGroup')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showAddGroupModal && (
        <EditLubricantGroupModal
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingGroup}
          isSubmitting={isCreating || isUpdating}
        />
      )}
    </Stack>
  );
};

export default LubricantGroupsContainer;
